/**
 * Assets Module
 * Handles asset management UI and operations
 */

import { API_BASE_URL } from '../core/config.js';
import { showError, showSuccess } from '../core/ui.js';
import { t } from '../core/i18n.js';
import { auth } from '../core/auth.js';
import { updatePageTitle, updateActiveNavigation } from './navigation.js';
import { showAssetModal, deleteAsset as deleteAssetModal } from './assets-modals.js';

// State management
const assetsState = {
    assets: [],
    filteredAssets: [],
    searchTerm: '',
    selectedAccount: 'all',
    currentPage: 1,
    itemsPerPage: 50,
    isLoading: false
};

/**
 * Display the assets page
 */
export async function showAssets() {
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    if (!auth.isAuthenticated()) {
        showError('Please login to view assets');
        return;
    }

    // Show the content area
    mainContent.style.display = 'block';
    
    // Update navigation and page title
    updateActiveNavigation('assets');
    updatePageTitle(t('assets.title'));
    
    // Initialize the assets view
    await initAssets();
}

/**
 * Initialize assets view
 */
export async function initAssets() {
    console.log('🏗️ Initializing Assets view...');
    
    const mainContent = document.getElementById('main-content');
    if (!mainContent) {
        console.error('❌ main-content element not found');
        return;
    }

    mainContent.innerHTML = `
        <!-- Enhanced Filter Row - Sticky -->
        <div class="sticky top-16 z-30 mb-0 bg-gray-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3">
            <div class="bg-gray-200 shadow rounded-lg p-4">
                <!-- Row 1: Add Asset Button -->
                <div class="flex justify-end mb-2">
                    <button onclick="window.showCreateAssetModal()" 
                            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 shadow-sm">
                        <svg class="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        ${t('assets.create')}
                    </button>
                </div>
                
                <!-- Row 2: Filters and Action Buttons -->
                <div class="flex items-end justify-between gap-4">
                    <!-- Left side: Search and Account Filter -->
                    <div class="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Search Field -->
                        <div>
                            <label for="asset-search" class="block text-sm font-medium text-gray-700 mb-1">${t('assets.search')}</label>
                            <input type="text" id="asset-search" placeholder="${t('assets.searchPlaceholder')}" 
                                   class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                   oninput="window.filterAssets()">
                        </div>
                        
                        <!-- Account Filter -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">${t('assets.filterByAccount')}</label>
                            <div class="relative">
                                <button type="button" id="assets-account-filter-toggle" 
                                        onclick="window.toggleAssetsAccountFilterExpanded()"
                                        class="w-full text-left border border-gray-300 rounded-md px-3 py-2 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                                    <div class="flex items-center justify-between">
                                        <span id="assets-account-filter-display" class="text-sm text-gray-700">${t('assets.anyAccount')}</span>
                                        <svg id="assets-account-filter-arrow" class="w-4 h-4 text-gray-400 transform transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                        </svg>
                                    </div>
                                </button>
                                <div id="assets-account-filter-tree-container" class="hidden absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                                    <div id="assets-account-filter-tree" class="p-2 max-h-64 overflow-y-auto">
                                        <div class="text-gray-400 text-sm">${t('assets.loadingAccounts')}</div>
                                    </div>
                                    <div class="border-t border-gray-200 px-3 py-2 bg-gray-50 flex items-center justify-end">
                                        <button type="button" onclick="window.clearAssetsAccountFilter()" class="text-sm text-gray-600 hover:text-gray-700">
                                            ${t('assets.clearAll')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Right side: Action buttons -->
                    <div class="flex items-end space-x-2">
                        <button onclick="window.clearAssetsFilters()" 
                                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            ${t('assets.clear')}
                        </button>
                        <button onclick="window.loadAssets(true)" 
                                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            <svg class="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            ${t('assets.refresh')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Assets Count Display -->
        <div class="bg-white shadow rounded-lg mt-3 px-6 py-3">
            <div id="assets-count-display" class="text-sm text-gray-600">
                <span class="font-medium">Loading assets...</span>
            </div>
        </div>
        
        <!-- Assets List -->
        <div class="bg-white shadow rounded-lg mt-3">
            <div id="assets-list">
                <div class="text-center py-4">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p class="mt-2 text-sm text-gray-500">Loading assets...</p>
                </div>
            </div>
        </div>
    `;

    // Setup event listeners
    setupAssetEventListeners();

    // Load data
    await loadAssets();
}

/**
 * Setup event listeners
 */
function setupAssetEventListeners() {
    // Global functions for inline event handlers
    window.filterAssets = filterAssets;
    window.loadAssets = loadAssets;
    window.clearAssetsFilters = clearAssetsFilters;
    window.toggleAssetsAccountFilterExpanded = toggleAssetsAccountFilterExpanded;
    window.clearAssetsAccountFilter = clearAssetsAccountFilter;
    window.toggleAssetsAccountFilterTreeItem = toggleAssetsAccountFilterTreeItem;
    window.selectAssetAccountFilter = selectAssetAccountFilter;
    window.showCreateAssetModal = showCreateAssetModal;
    window.editAsset = editAsset;
    window.deleteAsset = deleteAsset;
}

/**
 * Load assets from API
 */
export async function loadAssets() {
    if (assetsState.isLoading) return;
    
    assetsState.isLoading = true;
    
    try {
        const response = await fetch(`${API_BASE_URL}/assets/`, {
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`
            }
        });

        if (!response.ok) {
            throw new Error('Failed to load assets');
        }

        const data = await response.json();
        console.log('Loaded assets:', data);
        // Backend now returns {assets, total}; keep backward compat with array shape.
        assetsState.assets = Array.isArray(data) ? data : (data.assets || data.items || []);
        
        filterAssets();
        
    } catch (error) {
        console.error('Error loading assets:', error);
        showError('Failed to load assets');
        showEmptyState('Failed to load assets');
    } finally {
        assetsState.isLoading = false;
    }
}

/**
 * Load accounts for filter dropdown (SuperAdmin only)
 */
async function loadAccountsForFilter() {
    try {
        const response = await fetch(`${API_BASE_URL}/accounts`, {
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`
            }
        });

        if (!response.ok) return;

        const accounts = await response.json();
        const filterContainer = document.getElementById('asset-account-filter-container');
        const filterSelect = document.getElementById('asset-account-filter');
        
        if (filterContainer && filterSelect) {
            filterContainer.style.display = 'block';
            
            // Clear and rebuild options
            filterSelect.innerHTML = '<option value="all">All Accounts</option>';
            accounts.forEach(account => {
                const option = document.createElement('option');
                option.value = account._id;
                option.textContent = account.name || account.account_name || 'Unnamed Account';
                filterSelect.appendChild(option);
            });
        }
    } catch (error) {
        console.error('Error loading accounts:', error);
    }
}

/**
 * Filter assets based on search and account
 */
function filterAssets() {
    const searchInput = document.getElementById('asset-search');
    
    assetsState.searchTerm = searchInput?.value.toLowerCase() || '';

    // Apply filters
    assetsState.filteredAssets = assetsState.assets.filter(asset => {
        // Search filter
        const matchesSearch = !assetsState.searchTerm || 
            asset.name?.toLowerCase().includes(assetsState.searchTerm) ||
            asset.home_location_name?.toLowerCase().includes(assetsState.searchTerm) ||
            asset.account_name?.toLowerCase().includes(assetsState.searchTerm);

        // Account filter (check selected account from hierarchy)
        const matchesAccount = assetsState.selectedAccount === 'all' || 
            asset.account_id === assetsState.selectedAccount;

        return matchesSearch && matchesAccount;
    });

    renderAssets();
}

/**
 * Render assets list as table
 */
function renderAssets() {
    const listContainer = document.getElementById('assets-list');
    const countDisplay = document.getElementById('assets-count-display');
    
    if (!listContainer) return;

    // Update count
    if (countDisplay) {
        countDisplay.innerHTML = `<span class="font-medium">${t('common.showing')} ${assetsState.filteredAssets.length} ${t('common.assets')}</span>`;
    }

    if (assetsState.filteredAssets.length === 0) {
        showEmptyState();
        return;
    }

    // Render table
    listContainer.innerHTML = `
        <div class="shadow ring-1 ring-black ring-opacity-5 md:rounded-lg overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-300">
                <thead class="bg-gray-200">
                    <tr>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-200">
                            ${t('assets.name')}
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-200">
                            ${t('assets.assetId')}
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-200">
                            ${t('assets.account')}
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-200">
                            ${t('assets.homeLocation')}
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-200">
                            ${t('assets.sensors')}
                        </th>
                        <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-200">
                            ${t('common.actions')}
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    ${generateAssetRows(assetsState.filteredAssets)}
                </tbody>
            </table>
        </div>
    `;
}

/**
 * Generate asset table rows
 */
function generateAssetRows(assets) {
    if (!assets || assets.length === 0) {
        return `
            <tr>
                <td colspan="6" class="px-6 py-12 text-center text-gray-500">
                    ${t('assets.noAssets')}
                </td>
            </tr>
        `;
    }

    return assets.map(asset => {
        const assetId = asset.id || asset._id;
        const accountName = asset.account_name || 'Unknown Account';
        const homeLocation = asset.home_location_name || asset.home_location?.name || '-';
        const sensorCount = (asset.sensor_ids && asset.sensor_ids.length) || (asset.sensors && asset.sensors.length) || 0;

        return `
            <tr class="hover:bg-gray-50">
                <td class="px-6 py-4 text-sm">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 h-8 w-8">
                            <div class="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                                <svg class="h-4 w-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                                </svg>
                            </div>
                        </div>
                        <div class="ml-4">
                            <button onclick="window.editAsset('${assetId}')" class="text-sm font-medium text-primary-600 hover:text-primary-900">${asset.name || 'Unnamed Asset'}</button>
                        </div>
                    </div>
                </td>
                <td class="px-6 py-4 text-sm text-gray-500">
                    ${asset.asset_id || '-'}
                </td>
                <td class="px-6 py-4 text-sm text-gray-900">
                    <div class="flex items-center">
                        <svg class="h-4 w-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                        </svg>
                        ${accountName}
                    </div>
                </td>
                <td class="px-6 py-4 text-sm text-gray-500">
                    ${homeLocation}
                </td>
                <td class="px-6 py-4 text-sm text-gray-500">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${sensorCount > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}">
                        ${sensorCount} ${sensorCount === 1 ? 'sensor' : 'sensors'}
                    </span>
                </td>
                <td class="px-6 py-4 text-right text-sm font-medium">
                    <button onclick="window.deleteAsset('${assetId}', '${(asset.name || 'this asset').replace(/'/g, "\\'")}  ')" class="text-red-600 hover:text-red-900">
                        ${t('common.delete')}
                    </button>
                </td>
            </tr>
        `;
    }).join('');
}

/**
 * Show empty state
 */
function showEmptyState(message = null) {
    const listContainer = document.getElementById('assets-list');
    if (!listContainer) return;

    listContainer.innerHTML = `
        <div class="p-12 text-center">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">${message || t('assets.noAssets')}</h3>
            <p class="mt-1 text-sm text-gray-500">${message ? '' : t('assets.noAssetsDescription')}</p>
            ${!message ? `
                <div class="mt-6">
                    <button onclick="window.showCreateAssetModal()" 
                            class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700">
                        <svg class="mr-2 -ml-1 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                        </svg>
                        ${t('assets.create')}
                    </button>
                </div>
            ` : ''}
        </div>
    `;
}

/**
 * Clear all filters
 */
function clearAssetsFilters() {
    // Clear search
    const searchInput = document.getElementById('asset-search');
    if (searchInput) searchInput.value = '';
    
    // Clear account filter
    assetsState.selectedAccount = 'all';
    const filterDisplay = document.getElementById('assets-account-filter-display');
    if (filterDisplay) filterDisplay.textContent = t('assets.anyAccount');
    
    // Reset and reload
    assetsState.searchTerm = '';
    filterAssets();
}

/**
 * Toggle account filter dropdown
 */
function toggleAssetsAccountFilterExpanded() {
    const container = document.getElementById('assets-account-filter-tree-container');
    const arrow = document.getElementById('assets-account-filter-arrow');
    
    if (!container) return;
    
    if (container.classList.contains('hidden')) {
        container.classList.remove('hidden');
        if (arrow) arrow.classList.add('rotate-180');
        loadAccountHierarchyForFilter();
    } else {
        container.classList.add('hidden');
        if (arrow) arrow.classList.remove('rotate-180');
    }
}

/**
 * Load account hierarchy for filter
 */
async function loadAccountHierarchyForFilter() {
    const treeContainer = document.getElementById('assets-account-filter-tree');
    if (!treeContainer) return;
    
    try {
        const response = await fetch(`${API_BASE_URL}/accounts/hierarchy`, {
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`
            }
        });
        
        if (!response.ok) throw new Error('Failed to load accounts');
        
        const accounts = await response.json();
        treeContainer.innerHTML = renderAccountFilterTree(accounts);
    } catch (error) {
        console.error('Error loading accounts:', error);
        treeContainer.innerHTML = '<div class="text-red-500 text-sm">Failed to load accounts</div>';
    }
}

/**
 * Render account filter tree
 */
function renderAccountFilterTree(accounts, level = 0) {
    if (!accounts || accounts.length === 0) return '';
    
    return accounts.map(account => {
        const hasChildren = account.children && account.children.length > 0;
        const accountId = account.id || account._id;
        const isSelected = assetsState.selectedAccount === accountId;
        
        return `
            <div class="account-filter-item" style="margin-left: ${level * 16}px">
                <div class="flex items-center py-1 px-2 hover:bg-gray-100 rounded cursor-pointer"
                     onclick="window.selectAssetAccountFilter('${accountId}', '${account.name.replace(/'/g, "\\'")}')">
                    ${hasChildren ? `
                        <button onclick="event.stopPropagation(); window.toggleAssetsAccountFilterTreeItem('${accountId}')"
                                class="mr-1 focus:outline-none">
                            <svg class="w-4 h-4 text-gray-400 account-tree-arrow-${accountId}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                        </button>
                    ` : '<span class="w-5"></span>'}
                    <span class="text-sm ${isSelected ? 'font-medium text-primary-600' : 'text-gray-700'}">${account.name}</span>
                </div>
                <div id="account-filter-children-${accountId}" class="hidden">
                    ${hasChildren ? renderAccountFilterTree(account.children, level + 1) : ''}
                </div>
            </div>
        `;
    }).join('');
}

/**
 * Toggle account tree item
 */
function toggleAssetsAccountFilterTreeItem(accountId) {
    const childrenContainer = document.getElementById(`account-filter-children-${accountId}`);
    const arrow = document.querySelector(`.account-tree-arrow-${accountId}`);
    
    if (childrenContainer && arrow) {
        if (childrenContainer.classList.contains('hidden')) {
            childrenContainer.classList.remove('hidden');
            arrow.classList.add('rotate-90');
        } else {
            childrenContainer.classList.add('hidden');
            arrow.classList.remove('rotate-90');
        }
    }
}

/**
 * Select account filter
 */
function selectAssetAccountFilter(accountId, accountName) {
    assetsState.selectedAccount = accountId;
    
    const filterDisplay = document.getElementById('assets-account-filter-display');
    if (filterDisplay) {
        filterDisplay.textContent = accountName;
    }
    
    // Close dropdown
    const container = document.getElementById('assets-account-filter-tree-container');
    const arrow = document.getElementById('assets-account-filter-arrow');
    if (container) container.classList.add('hidden');
    if (arrow) arrow.classList.remove('rotate-180');
    
    // Apply filter
    filterAssets();
}

/**
 * Clear account filter
 */
function clearAssetsAccountFilter() {
    assetsState.selectedAccount = 'all';
    
    const filterDisplay = document.getElementById('assets-account-filter-display');
    if (filterDisplay) {
        filterDisplay.textContent = t('assets.anyAccount');
    }
    
    filterAssets();
}/**
 * Show create asset modal
 */
function showCreateAssetModal() {
    showAssetModal();
}

/**
 * Edit asset
 */
function editAsset(assetId) {
    showAssetModal(assetId);
}

/**
 * Delete asset (delegated to modal)
 */
async function deleteAsset(assetId, assetName) {
    await deleteAssetModal(assetId, assetName);
}
